import React from "react";
import { Lock, ShieldCheck, MapPinned, BadgeCheck } from "lucide-react";
import { PRIVACY, PRIVACY_DETAILS, PRIVACY_UPDATED, CONTACT_EMAIL } from "../../data/site";
import { Reveal } from "./Reveal";

const ICONS = [Lock, ShieldCheck, MapPinned, BadgeCheck];

const EMAIL = "hello@sistersafe.app";

// Renders paragraph text, turning the support email into a clickable mailto link.
const withEmailLinks = (text) =>
  text.split(EMAIL).reduce((acc, part, i, arr) => {
    acc.push(<React.Fragment key={`t-${i}`}>{part}</React.Fragment>);
    if (i < arr.length - 1) {
      acc.push(
        <a
          key={`a-${i}`}
          href={`mailto:${EMAIL}`}
          className="font-medium text-[#2F5E43] underline decoration-[#2F5E43]/30 underline-offset-2 transition-colors hover:decoration-[#2F5E43]"
          data-testid="privacy-email-link"
        >
          {EMAIL}
        </a>
      );
    }
    return acc;
  }, []);

export const Privacy = () => (
  <section id="privacy" className="relative py-24 md:py-32" data-testid="privacy-section">
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <div className="rounded-[2.5rem] border border-[#2F5E43]/10 bg-[#E4EDE6]/40 p-10 md:p-16">
        <div className="max-w-2xl">
          <Reveal>
            <p className="text-sm font-semibold uppercase tracking-[0.15em] text-[#8AA583]">
              Privacy & Trust
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-heading mt-5 text-4xl font-semibold leading-[1.15] tracking-tight text-[#111E16] md:text-5xl">
              Your safety, your data, your control.
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-5 text-lg leading-relaxed text-[#4A5F52]">
              Trust is the foundation of everything we build. SisterSafe is private by
              design, so you're always the one in charge.
            </p>
          </Reveal>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {PRIVACY.map((p, i) => {
            const Icon = ICONS[i];
            return (
              <Reveal key={p.title} delay={(i % 2) * 0.1}>
                <div className="flex gap-4 rounded-3xl bg-white p-7 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#E4EDE6] text-[#2F5E43]">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-[#111E16]">
                      {p.title}
                    </h3>
                    <p className="mt-1.5 leading-relaxed text-[#4A5F52]">{p.desc}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>

      {/* Detailed privacy information */}
      <div className="mx-auto mt-16 max-w-3xl md:mt-20" data-testid="privacy-details">
        <Reveal>
          <h3 className="font-heading text-3xl font-semibold tracking-tight text-[#111E16] md:text-4xl">
            Privacy Information
          </h3>
        </Reveal>
        <Reveal delay={0.06}>
          <p className="mt-3 text-sm text-[#8AA583]" data-testid="privacy-last-updated">
            Last updated: {PRIVACY_UPDATED}
          </p>
        </Reveal>

        <div className="mt-10 space-y-12">
          {PRIVACY_DETAILS.map((sec) => (
            <Reveal key={sec.heading}>
              <div>
                <h4 className="font-heading text-xl font-semibold text-[#111E16] md:text-2xl">
                  {sec.heading}
                </h4>
                <div className="mt-4 space-y-4">
                  {sec.paragraphs.map((p, i) => (
                    <p
                      key={i}
                      className="text-base leading-relaxed text-[#4A5F52] md:text-lg"
                    >
                      {withEmailLinks(p)}
                    </p>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}

          <Reveal>
            <div>
              <h4 className="font-heading text-xl font-semibold text-[#111E16] md:text-2xl">
                Contact Us
              </h4>
              <p className="mt-4 text-base leading-relaxed text-[#4A5F52] md:text-lg">
                If you have questions about this privacy information, your personal
                information, account deletion, or a privacy request, contact:
              </p>
              <div className="mt-4 space-y-1 text-base leading-relaxed text-[#4A5F52] md:text-lg">
                <p className="font-medium text-[#111E16]">SisterSafe</p>
                <p>
                  Email:{" "}
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="font-medium text-[#2F5E43] underline decoration-[#2F5E43]/30 underline-offset-2 transition-colors hover:decoration-[#2F5E43]"
                  >
                    {CONTACT_EMAIL}
                  </a>
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
